import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";
import {
  ACCENT_SWATCHES,
  MAX_AVATAR_BYTES,
  MAX_CHANNEL_NAME_LENGTH,
  MAX_TOPIC_LENGTH,
  OWNER_ROLE_ID,
  PERMISSIONS,
  ROLE_SWATCHES,
  formatBytes,
  isTimedOut,
  normalizeChannelName,
} from "@shared/protocol";
import type { AuditLogEntry, ChannelKind, PermissionId } from "@shared/types";
import { C, FONT_MONO, FONT_SANS, hexA, initialOf } from "@/theme";
import type { SettingsTab } from "@/state/appTypes";
import { can, colorOf, roleMembers, serverMembers, useApp } from "@/state/store";
import { Ajuda } from "@/components/Ajuda";
import { Atualizacao } from "@/components/Atualizacao";
import { Historia } from "@/components/Historia";
import { CloseIcon, HashIcon, MicIcon, SpeakerIcon } from "@/components/icons";
import { AccountTab, SessionsTab } from "@/components/AccountSettings";

/**
 * As abas da CONTA são de quem está usando o app: valem em qualquer lugar e
 * não dependem de cargo nenhum. Por isso vêm primeiro e nunca são escondidas.
 */
const ACCOUNT_TABS_BASE: { id: SettingsTab; label: string }[] = [
  { id: "conta", label: "Minha conta" },
  { id: "sessoes", label: "Sessões ativas" },
  { id: "historia", label: "História" },
  { id: "ajuda", label: "Ajuda" },
  { id: "atualizacao", label: "Atualização" },
];

/**
 * As abas da conta, com a de Administracao SO para fundador.
 *
 * Esconder e conveniencia: quem nao e fundador nao ve a aba, e se forjar o
 * evento do painel bate na checagem do servidor, que e onde a regra mora.
 */
function accountTabs(fundador: boolean): { id: SettingsTab; label: string }[] {
  return fundador
    ? [...ACCOUNT_TABS_BASE, { id: "administracao" as SettingsTab, label: "Administração" }]
    : ACCOUNT_TABS_BASE;
}

/** As da COMUNIDADE são administrativas e cada uma exige a sua permissão. */
const SERVER_TABS: { id: SettingsTab; label: string }[] = [
  { id: "geral", label: "Visão geral" },
  { id: "canais", label: "Canais" },
  { id: "cargos", label: "Cargos" },
  { id: "membros", label: "Membros" },
  { id: "auditoria", label: "Auditoria" },
];

const TITLES: Record<SettingsTab, string> = {
  conta: "Minha conta",
  sessoes: "Sessões ativas",
  historia: "Por que o LFRCORD existe",
  ajuda: "Ajuda — perguntas frequentes",
  atualizacao: "Versão e atualização",
  administracao: "Administração",
  geral: "Visão geral",
  canais: "Canais e salas de voz",
  cargos: "Cargos e permissões",
  membros: "Membros",
  auditoria: "Log de moderação",
};

/**
 * As descricoes de `PERMISSIONS` vivem no contrato compartilhado sem acentos;
 * a interface segue o desenho, entao as acentuadas ficam aqui.
 */
const PERM_DESC: Partial<Record<PermissionId, string>> = {
  cargos: "Editar cargos e permissões",
};

const MONO_LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
};

const FIELD: CSSProperties = {
  padding: "10px 12px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  background: C.field,
  color: C.text,
  fontSize: 14,
  outline: 0,
};

const SMALL_FIELD: CSSProperties = {
  ...FIELD,
  padding: "8px 10px",
  borderRadius: 8,
  fontSize: 13,
};

const GHOST_BTN: CSSProperties = {
  padding: "6px 12px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 8,
  background: C.field,
  color: C.textSoft,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const DANGER_BTN: CSSProperties = {
  padding: "6px 12px",
  border: `1px solid ${hexA(C.red, 0.4)}`,
  borderRadius: 8,
  background: hexA(C.red, 0.12),
  color: C.red,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const ACCENT_BTN: CSSProperties = {
  padding: "6px 12px",
  border: 0,
  borderRadius: 8,
  background: "var(--ac, #1F4FD8)",
  color: C.ink,
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

const ICON_BTN: CSSProperties = {
  width: 30,
  height: 30,
  flex: "0 0 auto",
  display: "grid",
  placeItems: "center",
  padding: 0,
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 8,
  background: "transparent",
  color: C.textMuted,
};

type ChannelDraft = { kind: ChannelKind; id: string; name: string; topic: string };
type ChannelTarget = { kind: ChannelKind; id: string };
type MemberAction = "mute" | "kick" | "ban" | "timeout";

/** Presets de duração de timeout, igual ao teto do Discord (28 dias). */
const TIMEOUT_PRESETS: { label: string; ms: number }[] = [
  { label: "60 segundos", ms: 60 * 1000 },
  { label: "5 minutos", ms: 5 * 60 * 1000 },
  { label: "10 minutos", ms: 10 * 60 * 1000 },
  { label: "1 hora", ms: 60 * 60 * 1000 },
  { label: "1 dia", ms: 24 * 60 * 60 * 1000 },
  { label: "7 dias", ms: 7 * 24 * 60 * 60 * 1000 },
];
type MemberConfirm = { userId: string; action: MemberAction };

/** Espelha `AUDIT_LOG_PAGE_SIZE` de `server/db.ts` — só usado para saber se "carregar mais" ainda faz sentido. */
const AUDIT_PAGE_SIZE = 100;

const AUDIT_ACTION_META: Record<AuditLogEntry["action"], { label: string; color: string }> = {
  // Ações do Painel dos Fundadores: valem acima de qualquer servidor, e por
  // isso aparecem no log de todos eles.
  platform_ban: { label: "BANIDO DA PLATAFORMA", color: C.red },
  platform_unban: { label: "BANIMENTO REVOGADO", color: C.blue },
  platform_server_delete: { label: "SERVIDOR APAGADO", color: C.red },
  platform_password_reset: { label: "SENHA REDEFINIDA", color: C.amber },
  platform_sessions_end: { label: "SESSÕES ENCERRADAS", color: C.amber },
  platform_announce: { label: "AVISO ENVIADO", color: C.blue },
  platform_flag: { label: "INTERRUPTOR", color: C.amber },
  platform_acesso_negado: { label: "ACESSO NEGADO", color: C.red },
  platform_alerta: { label: "ALERTA", color: C.red },
  kick: { label: "EXPULS", color: C.red },
  ban: { label: "BANIMENTO", color: C.red },
  timeout: { label: "TIMEOUT", color: C.amber },
  server_mute: { label: "SILENCIADO", color: C.amber },
  server_unmute: { label: "VOZ DEVOLVIDA", color: C.green },
  server_deafen: { label: "ÁUDIO DESATIVADO", color: C.amber },
  server_undeafen: { label: "ÁUDIO DEVOLVIDO", color: C.green },
  voice_disconnect: { label: "DESCONECTADO DA CHAMADA", color: C.amber },
  voice_move: { label: "MOVIDO DE SALA", color: C.blue },
  role_change: { label: "CARGO ALTERADO", color: C.blue },
  message_delete: { label: "MENSAGEM APAGADA", color: C.textMuted },
  channel_create: { label: "CANAL CRIADO", color: C.green },
  channel_update: { label: "CANAL EDITADO", color: C.blue },
  channel_delete: { label: "CANAL APAGADO", color: C.red },
};

/** Handle sempre exibido com arroba, mesmo se o banco guardar sem. */
function atHandle(handle: string): string {
  return handle.startsWith("@") ? handle : `@${handle}`;
}

function dimIf(allowed: boolean): CSSProperties {
  return allowed ? {} : { opacity: 0.55 };
}

function cursorIf(allowed: boolean): CSSProperties["cursor"] {
  return allowed ? "pointer" : "not-allowed";
}

/* Icones locais: esta fatia nao e dona de `icons.tsx`, entao os glifos novos
   sao desenhados aqui no mesmo traco (1.7, pontas arredondadas). */

function PencilIcon({ size = 14 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 20h4L19 9l-4-4L4 16v4z" />
      <path d="M14 6l4 4" />
    </svg>
  );
}

function TrashIcon({ size = 14 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6.5h16 M9.5 6.5V4.2h5v2.3 M6.8 6.5l1 13h8.4l1-13" />
      <path d="M10.4 10v6 M13.6 10v6" />
    </svg>
  );
}

function PlusIcon({ size = 14 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    >
      <path d="M12 5v14 M5 12h14" />
    </svg>
  );
}

function KickIcon({ size = 15 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 4h4.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H14" />
      <path d="M9.5 8.5 6 12l3.5 3.5 M6 12h9" />
    </svg>
  );
}

function BanIcon({ size = 15 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M6.4 6.4 17.6 17.6" />
    </svg>
  );
}

function TimeoutIcon({ size = 15 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

/** Chave de 40x22 do desenho, com o knob deslizando de 3 para 21. */
function Toggle({
  on,
  title,
  disabled = false,
  onToggle,
}: {
  on: boolean;
  title: string;
  disabled?: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={on}
      title={title}
      onClick={onToggle}
      style={{
        width: 40,
        height: 22,
        flex: "0 0 auto",
        position: "relative",
        padding: 0,
        border: 0,
        borderRadius: 999,
        cursor: cursorIf(!disabled),
        background: on ? "var(--ac, #1F4FD8)" : C.track,
        transition: "background 150ms ease",
        ...dimIf(!disabled),
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          width: 16,
          height: 16,
          borderRadius: 999,
          background: C.text,
          transition: "left 150ms ease",
          left: on ? 21 : 3,
        }}
      />
    </button>
  );
}

/**
 * Credito de quem desenvolveu o app. A folha de estilo pinta todo `a` de
 * laranja; aqui a cor vem inline para o rodape continuar discreto.
 */
function CreditLink(): ReactElement {
  const [hover, setHover] = useState(false);
  return (
    <a
      href="https://lfrcrew.dev"
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        color: hover ? "var(--ac, #1F4FD8)" : C.textLabel,
        textDecoration: hover ? "underline" : "none",
        textUnderlineOffset: 3,
        transition: "color 140ms ease",
      }}
    >
      lfrcrew.dev
    </a>
  );
}

/** Uma preferencia de aviso: titulo, explicacao curta e a chave a direita. */
function PrefRow({
  title,
  hint,
  on,
  disabled = false,
  onToggle,
}: {
  title: string;
  hint: string;
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "11px 2px",
        borderBottom: `1px solid ${C.lineRow}`,
      }}
    >
      <span style={{ flex: 1, minWidth: 0, ...dimIf(!disabled) }}>
        <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: C.textStrong }}>
          {title}
        </span>
        <span style={{ display: "block", fontSize: 11.5, color: C.textFaint, marginTop: 1 }}>
          {hint}
        </span>
      </span>
      <Toggle on={on} title={title} disabled={disabled} onToggle={onToggle} />
    </div>
  );
}

/** Foto do membro; sem foto (ou se ela falhar) cai na inicial colorida. */
function Avatar({
  url,
  name,
  color,
  size,
  fontSize,
}: {
  url: string | null;
  name: string;
  color: string;
  size: number;
  fontSize: number;
}): ReactElement {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [url]);

  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setBroken(true)}
        style={{
          width: size,
          height: size,
          flex: "0 0 auto",
          display: "block",
          borderRadius: "50%",
          objectFit: "cover",
          background: hexA(color, 0.18),
        }}
      />
    );
  }
  return (
    <span
      style={{
        width: size,
        height: size,
        flex: "0 0 auto",
        display: "grid",
        placeItems: "center",
        borderRadius: "50%",
        fontSize,
        fontWeight: 600,
        background: hexA(color, 0.18),
        color,
      }}
    >
      {initialOf(name)}
    </span>
  );
}

export function SettingsModal() {
  const { state, actions } = useApp();
  const canRoles = can(state, "cargos");
  const canChannels = can(state, "canais");
  const canKick = can(state, "expulsar");
  const canMute = can(state, "silenciar");
  const notifications = state.notifications;

  /**
   * Cada aba administrativa exige a permissão que ela usa.
   *
   * Antes, só "auditoria" era escondida: qualquer conta abria "Membros" e via
   * o cadastro inteiro com @handle, e "Cargos" listava quem tinha cada cargo.
   * Deixar os controles desabilitados não bastava — o vazamento estava no que
   * a tela MOSTRA, não no que ela deixa clicar.
   */
  const canModerate = canKick || canMute;
  const serverTabAllowed: Record<string, boolean> = {
    geral: true,
    canais: true,
    cargos: canRoles,
    membros: canModerate || canRoles,
    auditoria: canModerate || canRoles || canChannels,
  };
  const visibleServerTabs = SERVER_TABS.filter(tab => serverTabAllowed[tab.id] !== false);
  const abasDaConta = accountTabs(state.platformFounder);

  // Perder um cargo com a aba aberta não pode deixar a pessoa olhando para
  // algo que ela já não deveria ver.
  const currentAllowed =
    abasDaConta.some(tab => tab.id === state.settingsTab) ||
    serverTabAllowed[state.settingsTab] !== false;
  useEffect(() => {
    if (!currentAllowed) actions.setSettingsTab("conta");
  }, [actions, currentAllowed]);

  const tabButton = (tab: { id: SettingsTab; label: string }): ReactElement => {
    const active = state.settingsTab === tab.id;
    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => actions.setSettingsTab(tab.id)}
        className="hov-bg-1f"
        style={{
          textAlign: "left",
          padding: "8px 10px",
          border: 0,
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 13.5,
          fontWeight: 600,
          background: active ? C.selected : "transparent",
          color: active ? C.text : C.textMuted,
        }}
      >
        {tab.label}
      </button>
    );
  };

  const roles = [...state.roles].sort((a, b) => a.position - b.position);
  const selectedRole = state.roles.find(role => role.id === state.roleSel) ?? state.roles[0] ?? null;
  const textChannels = [...state.channels].sort((a, b) => a.position - b.position);
  const voiceChannels = [...state.voiceChannels].sort((a, b) => a.position - b.position);

  const [nameDraft, setNameDraft] = useState(state.settings.name);
  const [descDraft, setDescDraft] = useState(state.settings.description);
  const [iconDraft, setIconDraft] = useState(state.settings.icon);
  const [iconUrlDraft, setIconUrlDraft] = useState<string | null>(state.settings.iconUrl);
  const [iconBusy, setIconBusy] = useState(false);
  const [iconError, setIconError] = useState<string | null>(null);
  const [iconDrag, setIconDrag] = useState(false);
  const [iconBroken, setIconBroken] = useState(false);
  const [roleDraft, setRoleDraft] = useState<{ id: string; name: string }>({ id: "", name: "" });
  const [roleDeleting, setRoleDeleting] = useState(false);
  const [channelDraft, setChannelDraft] = useState<ChannelDraft | null>(null);
  const [channelDeleting, setChannelDeleting] = useState<ChannelTarget | null>(null);
  const [newText, setNewText] = useState({ name: "", topic: "" });
  const [newVoice, setNewVoice] = useState("");
  const [memberConfirm, setMemberConfirm] = useState<MemberConfirm | null>(null);
  const [timeoutMs, setTimeoutMs] = useState<number>(TIMEOUT_PRESETS[0].ms);
  const iconInputRef = useRef<HTMLInputElement | null>(null);

  const settingsName = state.settings.name;
  const settingsDesc = state.settings.description;
  const settingsIcon = state.settings.icon;
  const settingsIconUrl = state.settings.iconUrl;
  const settingsOpen = state.settingsOpen;
  const settingsTab = state.settingsTab;

  // Rascunhos voltam a espelhar o servidor sempre que ele responde ou o modal reabre.
  useEffect(() => {
    setNameDraft(settingsName);
    setDescDraft(settingsDesc);
    setIconDraft(settingsIcon);
    setIconUrlDraft(settingsIconUrl);
    setIconError(null);
  }, [settingsDesc, settingsIcon, settingsIconUrl, settingsName, settingsOpen]);

  // Uma imagem nova merece uma nova tentativa de carregamento.
  useEffect(() => {
    setIconBroken(false);
  }, [iconUrlDraft]);

  const selectedRoleId = selectedRole?.id ?? "";
  const selectedRoleName = selectedRole?.name ?? "";
  useEffect(() => {
    setRoleDraft({ id: selectedRoleId, name: selectedRoleName });
    setRoleDeleting(false);
  }, [selectedRoleId, selectedRoleName]);

  // Nenhuma confirmacao sobrevive a troca de aba nem ao fechamento do modal.
  useEffect(() => {
    setChannelDraft(null);
    setChannelDeleting(null);
    setMemberConfirm(null);
    setRoleDeleting(false);
  }, [settingsOpen, settingsTab]);

  const { closeSettings } = actions;
  useEffect(() => {
    if (!settingsOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSettings();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeSettings, settingsOpen]);

  // Abrir a aba de auditoria carrega a primeira página, uma vez por sessão do modal.
  const auditLog = state.auditLog;
  const auditLogLoading = state.auditLogLoading;
  const { requestAuditLog } = actions;
  useEffect(() => {
    if (settingsTab === "auditoria" && auditLog === null && !auditLogLoading) {
      requestAuditLog();
    }
  }, [settingsTab, auditLog, auditLogLoading, requestAuditLog]);

  if (!state.settingsOpen) return null;

  const dim = dimIf(canRoles);
  const clickable = cursorIf(canRoles);
  const dimChannels = dimIf(canChannels);
  const clickableChannels = cursorIf(canChannels);

  const memberCount = (roleId: string): number => roleMembers(state, roleId).length;

  /** Aviso de somente leitura conforme o que a aba atual exige. */
  const readOnlyNotice = (): string | null => {
    if (settingsTab === "canais") {
      return canChannels ? null : "SOMENTE LEITURA · VOC N GERENCIA CANAIS";
    }
    if (settingsTab === "membros") {
      if (canRoles || canKick || canMute) return null;
      return "SOMENTE LEITURA · VOC N MODERA MEMBROS";
    }
    if (canRoles) return null;
    // A aba "geral" usa a mesma permissao dos cargos, mas o aviso precisa falar
    // do que esta bloqueado ali: os dados da comunidade, nao os cargos.
    return settingsTab === "geral"
      ? "SOMENTE LEITURA · VOC N GERENCIA A COMUNIDADE"
      : "SOMENTE LEITURA · VOC N GERENCIA CARGOS";
  };

  const receiveIcon = async (file: File | undefined): Promise<void> => {
    // Um envio de cada vez: dois arquivos em corrida trocariam o icone pelo
    // que terminasse por ultimo, que nao e necessariamente o ultimo escolhido.
    if (!file || !canRoles || iconBusy) return;
    if (!file.type.startsWith("image/")) {
      setIconError("Escolha um arquivo de imagem.");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setIconError(`A imagem precisa ter no máximo ${formatBytes(MAX_AVATAR_BYTES)}.`);
      return;
    }
    setIconBusy(true);
    setIconError(null);
    try {
      setIconUrlDraft(await actions.uploadImage(file));
    } catch (failure) {
      setIconError(
        failure instanceof Error ? failure.message : "Não foi possível enviar a imagem.",
      );
    } finally {
      setIconBusy(false);
    }
  };

  const saveOverview = () => {
    if (!canRoles) return;
    const name = nameDraft.trim() || state.settings.name;
    const icon = (iconDraft.trim() || initialOf(name)).slice(0, 1).toUpperCase();
    actions.saveSettings({
      name,
      description: descDraft.trim(),
      icon,
      iconUrl: iconUrlDraft,
      accent: state.settings.accent,
    });
  };

  const commitRoleName = () => {
    if (!canRoles) return;
    const target = state.roles.find(role => role.id === roleDraft.id);
    if (!target) return;
    const name = roleDraft.name.trim();
    // Nome vazio nao vira cargo sem nome: o campo volta ao valor do servidor.
    if (!name) {
      setRoleDraft({ id: target.id, name: target.name });
      return;
    }
    if (name === target.name) return;
    actions.updateRole(target.id, { name });
  };

  const commitChannelDraft = () => {
    if (!channelDraft || !canChannels) return;
    const raw = channelDraft.name;
    const name =
      channelDraft.kind === "text"
        ? normalizeChannelName(raw)
        : raw.trim().slice(0, MAX_CHANNEL_NAME_LENGTH);
    if (!name) {
      setChannelDraft(null);
      return;
    }
    actions.updateChannel(
      channelDraft.kind,
      channelDraft.id,
      channelDraft.kind === "text"
        ? { name, topic: channelDraft.topic.trim().slice(0, MAX_TOPIC_LENGTH) }
        : { name },
    );
    setChannelDraft(null);
  };

  const createText = () => {
    if (!canChannels) return;
    const name = normalizeChannelName(newText.name);
    if (!name) return;
    actions.createChannel("text", name, newText.topic.trim().slice(0, MAX_TOPIC_LENGTH));
    setNewText({ name: "", topic: "" });
  };

  const createVoice = () => {
    if (!canChannels) return;
    const name = newVoice.trim().slice(0, MAX_CHANNEL_NAME_LENGTH);
    if (!name) return;
    actions.createChannel("voice", name);
    setNewVoice("");
  };

  const confirmChannelDelete = () => {
    if (!channelDeleting || !canChannels) return;
    actions.deleteChannel(channelDeleting.kind, channelDeleting.id);
    setChannelDeleting(null);
  };

  /** Uma linha de canal: leitura, edicao no lugar ou confirmacao de remocao. */
  const channelRow = (
    kind: ChannelKind,
    id: string,
    name: string,
    topic: string,
    canDelete: boolean,
  ): ReactElement => {
    const editing = channelDraft?.kind === kind && channelDraft.id === id;
    const deleting = channelDeleting?.kind === kind && channelDeleting.id === id;
    return (
      <div
        key={`${kind}:${id}`}
        style={{ padding: "10px 2px", borderBottom: `1px solid ${C.lineRow}` }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <span style={{ flex: "0 0 auto", display: "grid", placeItems: "center", color: C.textLabel }}>
            {kind === "text" ? <HashIcon size={15} /> : <SpeakerIcon size={15} />}
          </span>

          {editing && channelDraft ? (
            <>
              <span style={{ flex: 1, minWidth: 0, display: "grid", gap: 6 }}>
                <input
                  autoFocus
                  value={channelDraft.name}
                  onChange={event =>
                    setChannelDraft({ ...channelDraft, name: event.target.value })
                  }
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitChannelDraft();
                    }
                    if (event.key === "Escape") {
                      event.preventDefault();
                      setChannelDraft(null);
                    }
                  }}
                  maxLength={MAX_CHANNEL_NAME_LENGTH}
                  aria-label="Nome"
                  style={SMALL_FIELD}
                />
                {kind === "text" && (
                  <input
                    value={channelDraft.topic}
                    onChange={event =>
                      setChannelDraft({ ...channelDraft, topic: event.target.value })
                    }
                    onKeyDown={event => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitChannelDraft();
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        setChannelDraft(null);
                      }
                    }}
                    maxLength={MAX_TOPIC_LENGTH}
                    placeholder="Tópico do canal"
                    aria-label="Tópico"
                    style={{ ...SMALL_FIELD, fontSize: 12.5, color: C.textSoft }}
                  />
                )}
              </span>
              <button type="button" onClick={commitChannelDraft} className="hov-bright-108" style={ACCENT_BTN}>
                Salvar
              </button>
              <button
                type="button"
                onClick={() => setChannelDraft(null)}
                className="hov-bg-26"
                style={GHOST_BTN}
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: C.textStrong,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {name}
                </span>
                {kind === "text" && topic && (
                  <span
                    style={{
                      display: "block",
                      fontSize: 11.5,
                      color: C.textFaint,
                      marginTop: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {topic}
                  </span>
                )}
              </span>
              <button
                type="button"
                title="Renomear"
                disabled={!canChannels}
                onClick={() => {
                  setChannelDeleting(null);
                  setChannelDraft({ kind, id, name, topic });
                }}
                className={canChannels ? "hov-bg-26-fg" : undefined}
                style={{ ...ICON_BTN, cursor: clickableChannels, ...dimChannels }}
              >
                <PencilIcon size={14} />
              </button>
              <button
                type="button"
                title={
                  canDelete
                    ? "Apagar"
                    : "A comunidade precisa de pelo menos um canal de texto."
                }
                disabled={!canChannels || !canDelete}
                onClick={() => {
                  setChannelDraft(null);
                  setChannelDeleting({ kind, id });
                }}
                className={canChannels && canDelete ? "hov-red" : undefined}
                style={{
                  ...ICON_BTN,
                  color: C.red,
                  borderColor: hexA(C.red, 0.32),
                  cursor: cursorIf(canChannels && canDelete),
                  ...dimIf(canChannels && canDelete),
                }}
              >
                <TrashIcon size={14} />
              </button>
            </>
          )}
        </div>

        {deleting && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 10,
              padding: "8px 10px",
              border: `1px solid ${hexA(C.red, 0.28)}`,
              borderRadius: 9,
              background: hexA(C.red, 0.08),
            }}
          >
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.textSoft }}>
              {kind === "text"
                ? `Apagar #${name}? Todas as mensagens do canal vão junto.`
                : `Apagar a sala ${name}? Quem estiver nela é desconectado.`}
            </span>
            <button type="button" onClick={confirmChannelDelete} className="hov-bright-110" style={DANGER_BTN}>
              Apagar
            </button>
            <button
              type="button"
              onClick={() => setChannelDeleting(null)}
              className="hov-bg-26"
              style={GHOST_BTN}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    );
  };

  const notice = readOnlyNotice();
  const roleMembersList = selectedRole ? roleMembers(state, selectedRole.id) : [];

  return (
    <div
      onClick={closeSettings}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,.62)",
        animation: "fadein 150ms ease",
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          width: "min(880px,92vw)",
          height: "min(620px,86vh)",
          display: "flex",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          overflow: "hidden",
          background: C.stage,
          animation: "pop 200ms ease",
        }}
      >
        <div
          style={{
            width: 210,
            flex: "0 0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: "18px 12px",
            borderRight: `1px solid ${C.line}`,
            background: C.panel,
          }}
        >
          <div
            style={{
              margin: "0 10px 10px",
              fontFamily: FONT_MONO,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: ".14em",
              color: C.textLabelDim,
            }}
          >
            MINHA CONTA
          </div>
          {abasDaConta.map(tab => tabButton(tab))}

          {visibleServerTabs.length > 0 ? (
            <>
              <div
                style={{
                  margin: "16px 10px 6px",
                  fontFamily: FONT_MONO,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: ".14em",
                  color: C.textLabelDim,
                }}
              >
                COMUNIDADE
              </div>
              {visibleServerTabs.map(tab => tabButton(tab))}
            </>
          ) : null}
          <span style={{ flex: 1 }} />
          <div
            style={{
              margin: "0 10px",
              fontFamily: FONT_MONO,
              fontSize: 9,
              letterSpacing: ".1em",
              color: C.textGhost,
              lineHeight: 1.6,
            }}
          >
            <div>LFRCORD · LFR CREW</div>
            <div style={{ letterSpacing: ".05em" }}>
              por <CreditLink />
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px 0",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700 }}>{TITLES[state.settingsTab]}</div>
            <button
              type="button"
              onClick={closeSettings}
              title="Fechar"
              className="hov-bg-26-f2"
              style={{
                width: 30,
                height: 30,
                display: "grid",
                placeItems: "center",
                padding: 0,
                border: `1px solid ${C.lineStrong}`,
                borderRadius: 999,
                cursor: "pointer",
                background: "transparent",
                color: C.textMuted,
              }}
            >
              <CloseIcon size={14} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px 24px" }}>
            {notice && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "8px 12px",
                  border: `1px solid ${hexA(C.amber, 0.28)}`,
                  borderRadius: 9,
                  background: hexA(C.amber, 0.1),
                  fontFamily: FONT_MONO,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: ".12em",
                  color: C.amber,
                }}
              >
                {notice}
              </div>
            )}

            {state.settingsTab === "conta" && <AccountTab />}
            {state.settingsTab === "sessoes" && <SessionsTab />}
            {state.settingsTab === "historia" && <Historia />}
            {state.settingsTab === "ajuda" && <Ajuda />}
            {state.settingsTab === "atualizacao" && <Atualizacao />}
            {state.settingsTab === "administracao" && state.platformFounder && (
              <EntradaDoPainel />
            )}

            {state.settingsTab === "geral" && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
                  <div
                    role="button"
                    tabIndex={canRoles ? 0 : -1}
                    title="Trocar o ícone da comunidade"
                    onClick={() => {
                      if (canRoles) iconInputRef.current?.click();
                    }}
                    onKeyDown={event => {
                      if (!canRoles) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        iconInputRef.current?.click();
                      }
                    }}
                    onDragOver={event => {
                      if (!canRoles) return;
                      event.preventDefault();
                      setIconDrag(true);
                    }}
                    onDragLeave={() => setIconDrag(false)}
                    onDrop={event => {
                      event.preventDefault();
                      setIconDrag(false);
                      void receiveIcon(event.dataTransfer.files[0]);
                    }}
                    style={{
                      width: 64,
                      height: 64,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 16,
                      overflow: "hidden",
                      cursor: clickable,
                      outline: iconDrag ? "2px solid var(--ac, #1F4FD8)" : "none",
                      outlineOffset: 2,
                      // Durante o envio o fundo escurece: "ENVIANDO" em cinza
                      // claro sobre o acento laranja nao teria contraste.
                      background:
                        iconBusy || (iconUrlDraft && !iconBroken)
                          ? C.field
                          : "var(--ac, #1F4FD8)",
                      color: C.ink,
                      fontFamily: FONT_SANS,
                      fontSize: 28,
                      fontWeight: 700,
                      ...dim,
                    }}
                  >
                    {iconBusy ? (
                      <span
                        style={{
                          fontFamily: FONT_MONO,
                          fontSize: 9,
                          letterSpacing: ".1em",
                          color: C.textSoft,
                        }}
                      >
                        ENVIANDO
                      </span>
                    ) : iconUrlDraft && !iconBroken ? (
                      <img
                        src={iconUrlDraft}
                        alt=""
                        onError={() => setIconBroken(true)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      iconDraft || initialOf(nameDraft)
                    )}
                  </div>
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={event => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      void receiveIcon(file);
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 10.5,
                        lineHeight: 1.6,
                        color: C.textLabel,
                      }}
                    >
                      ÍCONE DA COMUNIDADE
                      <br />
                      ARRASTE UMA IMAGEM OU CLIQUE PARA TROCAR
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9 }}
                    >
                      <input
                        value={iconDraft}
                        onChange={event =>
                          setIconDraft(event.target.value.slice(-1).toUpperCase())
                        }
                        readOnly={!canRoles}
                        aria-label="Letra do ícone"
                        style={{
                          ...FIELD,
                          width: 38,
                          padding: "6px 0",
                          borderRadius: 8,
                          textAlign: "center",
                          cursor: canRoles ? "text" : "not-allowed",
                          ...dim,
                        }}
                      />
                      <span
                        style={{
                          fontFamily: FONT_MONO,
                          fontSize: 9.5,
                          letterSpacing: ".1em",
                          color: C.textGhost,
                        }}
                      >
                        LETRA USADA SEM IMAGEM
                      </span>
                      {iconUrlDraft && (
                        <button
                          type="button"
                          disabled={!canRoles}
                          onClick={() => setIconUrlDraft(null)}
                          className={canRoles ? "hov-bg-26" : undefined}
                          style={{ ...GHOST_BTN, cursor: clickable, ...dim }}
                        >
                          Remover imagem
                        </button>
                      )}
                    </div>
                    {iconError && (
                      <div style={{ marginTop: 8, fontSize: 12, color: C.red }}>{iconError}</div>
                    )}
                  </div>
                </div>

                <div style={{ display: "grid", gap: 16, maxWidth: 440 }}>
                  <label style={{ display: "grid", gap: 7 }}>
                    <span style={MONO_LABEL}>NOME DA COMUNIDADE</span>
                    <input
                      value={nameDraft}
                      onChange={event => setNameDraft(event.target.value)}
                      readOnly={!canRoles}
                      style={{ ...FIELD, ...dim }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 7 }}>
                    <span style={MONO_LABEL}>DESCRIÇÃO</span>
                    <textarea
                      rows={3}
                      value={descDraft}
                      onChange={event => setDescDraft(event.target.value)}
                      readOnly={!canRoles}
                      style={{ ...FIELD, resize: "vertical", fontFamily: FONT_SANS, ...dim }}
                    />
                  </label>

                  <div>
                    <div style={{ ...MONO_LABEL, marginBottom: 8 }}>COR DE ACENTO</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {ACCENT_SWATCHES.map(color => (
                        <button
                          key={color}
                          type="button"
                          title={color}
                          disabled={!canRoles}
                          onClick={() => actions.updateSettings({ accent: color })}
                          style={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            borderRadius: 8,
                            cursor: clickable,
                            background: color,
                            border: `2px solid ${
                              color === state.settings.accent ? C.text : "transparent"
                            }`,
                            ...dim,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                      type="button"
                      onClick={saveOverview}
                      disabled={!canRoles || iconBusy}
                      className={canRoles && !iconBusy ? "hov-bright-108" : undefined}
                      style={{
                        padding: "10px 18px",
                        border: 0,
                        borderRadius: 9,
                        cursor: cursorIf(canRoles && !iconBusy),
                        background: "var(--ac, #1F4FD8)",
                        color: C.ink,
                        fontSize: 13,
                        fontWeight: 700,
                        ...dimIf(canRoles && !iconBusy),
                      }}
                    >
                      Salvar alterações
                    </button>
                    {state.saved && (
                      <span
                        style={{
                          fontFamily: FONT_MONO,
                          fontSize: 11,
                          color: C.green,
                          animation: "fadein 200ms ease",
                        }}
                      >
                        ALTERAÇÕES SALVAS
                      </span>
                    )}
                  </div>
                </div>

                {/* Preferencias de aviso: sao de quem esta usando o app, nao da
                    comunidade — por isso ficam fora do bloqueio de permissao. */}
                <div style={{ maxWidth: 440, marginTop: 26 }}>
                  <div style={{ height: 1, marginBottom: 18, background: C.lineSoft }} />
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                    <span style={MONO_LABEL}>AVISOS DO NAVEGADOR</span>
                    <span
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        letterSpacing: ".1em",
                        color: C.textGhost,
                      }}
                    >
                      VALEM S NESTE NAVEGADOR
                    </span>
                  </div>

                  <PrefRow
                    title="Avisar sobre menções e diretas"
                    hint="Um aviso do sistema quando a aba não estiver em foco."
                    on={notifications.enabled}
                    onToggle={() => actions.setNotificationsEnabled(!notifications.enabled)}
                  />
                  <PrefRow
                    title="Tocar um som curto"
                    hint="Um bipe discreto junto do aviso."
                    on={notifications.sound}
                    disabled={!notifications.enabled}
                    onToggle={() => actions.setNotificationSound(!notifications.sound)}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginTop: 12,
                      minHeight: 30,
                    }}
                  >
                    {notifications.permission === "granted" ? (
                      <span
                        style={{
                          fontFamily: FONT_MONO,
                          fontSize: 10,
                          letterSpacing: ".1em",
                          color: C.green,
                        }}
                      >
                        PERMISSÃO CONCEDIDA
                      </span>
                    ) : notifications.permission === "denied" ? (
                      <span style={{ fontSize: 12.5, lineHeight: 1.45, color: C.textSoft }}>
                        O navegador não está entregando avisos deste site. Libere a permissão nas
                        configurações dele para voltar a receber.
                      </span>
                    ) : (
                      <>
                        {/* A permissao so pode ser pedida a partir de um clique
                            do proprio membro — nunca no carregamento da pagina. */}
                        <button
                          type="button"
                          onClick={() => actions.requestNotificationPermission()}
                          className="hov-bg-26"
                          style={GHOST_BTN}
                        >
                          Permitir avisos
                        </button>
                        <span
                          style={{
                            fontFamily: FONT_MONO,
                            fontSize: 9.5,
                            letterSpacing: ".1em",
                            color: C.textGhost,
                          }}
                        >
                          O NAVEGADOR VAI PERGUNTAR
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {state.settingsTab === "canais" && (
              <div style={{ display: "grid", gap: 26, maxWidth: 560 }}>
                <div>
                  <div style={{ ...MONO_LABEL, marginBottom: 6 }}>
                    {`CANAIS DE TEXTO — ${textChannels.length}`}
                  </div>
                  {textChannels.length === 0 && (
                    <div style={{ padding: "10px 2px", fontSize: 12.5, color: C.textLabel }}>
                      Nenhum canal de texto.
                    </div>
                  )}
                  {textChannels.map(channel =>
                    channelRow(
                      "text",
                      channel.id,
                      channel.name,
                      channel.topic,
                      textChannels.length > 1,
                    ),
                  )}
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}
                  >
                    <input
                      value={newText.name}
                      onChange={event => setNewText({ ...newText, name: event.target.value })}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          createText();
                        }
                      }}
                      readOnly={!canChannels}
                      maxLength={MAX_CHANNEL_NAME_LENGTH}
                      placeholder="novo-canal"
                      aria-label="Nome do novo canal"
                      style={{ ...SMALL_FIELD, width: 160, ...dimChannels }}
                    />
                    <input
                      value={newText.topic}
                      onChange={event => setNewText({ ...newText, topic: event.target.value })}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          createText();
                        }
                      }}
                      readOnly={!canChannels}
                      maxLength={MAX_TOPIC_LENGTH}
                      placeholder="Tópico (opcional)"
                      aria-label="Tópico do novo canal"
                      style={{ ...SMALL_FIELD, flex: 1, minWidth: 0, ...dimChannels }}
                    />
                    <button
                      type="button"
                      disabled={!canChannels || !normalizeChannelName(newText.name)}
                      onClick={createText}
                      className={canChannels ? "hov-bright-108" : undefined}
                      style={{
                        ...ACCENT_BTN,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 12px",
                        cursor: cursorIf(canChannels && Boolean(normalizeChannelName(newText.name))),
                        ...dimIf(canChannels && Boolean(normalizeChannelName(newText.name))),
                      }}
                    >
                      <PlusIcon size={13} />
                      Criar canal
                    </button>
                  </div>
                </div>

                <div>
                  <div style={{ ...MONO_LABEL, marginBottom: 6 }}>
                    {`SALAS DE VOZ — ${voiceChannels.length}`}
                  </div>
                  {voiceChannels.length === 0 && (
                    <div style={{ padding: "10px 2px", fontSize: 12.5, color: C.textLabel }}>
                      Nenhuma sala de voz.
                    </div>
                  )}
                  {voiceChannels.map(channel =>
                    channelRow("voice", channel.id, channel.name, "", true),
                  )}
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}
                  >
                    <input
                      value={newVoice}
                      onChange={event => setNewVoice(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          createVoice();
                        }
                      }}
                      readOnly={!canChannels}
                      maxLength={MAX_CHANNEL_NAME_LENGTH}
                      placeholder="Nova sala"
                      aria-label="Nome da nova sala de voz"
                      style={{ ...SMALL_FIELD, flex: 1, minWidth: 0, ...dimChannels }}
                    />
                    <button
                      type="button"
                      disabled={!canChannels || !newVoice.trim()}
                      onClick={createVoice}
                      className={canChannels ? "hov-bright-108" : undefined}
                      style={{
                        ...ACCENT_BTN,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 12px",
                        cursor: cursorIf(canChannels && Boolean(newVoice.trim())),
                        ...dimIf(canChannels && Boolean(newVoice.trim())),
                      }}
                    >
                      <PlusIcon size={13} />
                      Criar sala
                    </button>
                  </div>
                </div>
              </div>
            )}

            {state.settingsTab === "cargos" && (
              <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 20 }}>
                <div>
                  {roles.map(role => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => actions.selectRole(role.id)}
                      className="hov-bg-24"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 9,
                        padding: "8px 10px",
                        marginBottom: 2,
                        border: 0,
                        borderRadius: 8,
                        cursor: "pointer",
                        textAlign: "left",
                        background: role.id === selectedRole?.id ? C.selected : "transparent",
                      }}
                    >
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          flex: "0 0 auto",
                          borderRadius: 3,
                          background: role.color,
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: C.textStrong,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {role.name}
                      </span>
                      <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.textLabel }}>
                        {memberCount(role.id)}
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => actions.addRole()}
                    disabled={!canRoles}
                    className={canRoles ? "hov-border" : undefined}
                    style={{
                      width: "100%",
                      marginTop: 8,
                      padding: "8px 10px",
                      border: `1px dashed ${C.track}`,
                      borderRadius: 8,
                      cursor: clickable,
                      background: "transparent",
                      color: C.textMuted,
                      fontSize: 12.5,
                      fontWeight: 600,
                      ...dim,
                    }}
                  >
                    + Novo cargo
                  </button>
                </div>

                {selectedRole && (
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 12,
                        marginBottom: 16,
                      }}
                    >
                      <label style={{ display: "grid", gap: 7, flex: 1, maxWidth: 280 }}>
                        <span style={MONO_LABEL}>NOME DO CARGO</span>
                        <input
                          value={
                            roleDraft.id === selectedRole.id ? roleDraft.name : selectedRole.name
                          }
                          onChange={event =>
                            setRoleDraft({ id: selectedRole.id, name: event.target.value })
                          }
                          onBlur={commitRoleName}
                          onKeyDown={event => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              commitRoleName();
                            }
                          }}
                          readOnly={!canRoles}
                          style={{ ...FIELD, padding: "9px 12px", ...dim }}
                        />
                      </label>
                      {selectedRole.id !== OWNER_ROLE_ID &&
                        (roleDeleting ? (
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span
                              style={{
                                fontFamily: FONT_MONO,
                                fontSize: 10,
                                letterSpacing: ".1em",
                                color: C.red,
                              }}
                            >
                              {roleMembersList.length === 0
                                ? "APAGAR CARGO?"
                                : `APAGAR CARGO? AFETA ${roleMembersList.length} MEMBRO${
                                    roleMembersList.length > 1 ? "S" : ""
                                  }`}
                            </span>
                            <button
                              type="button"
                              className="hov-bright-110"
                              style={DANGER_BTN}
                              onClick={() => {
                                actions.deleteRole(selectedRole.id);
                                setRoleDeleting(false);
                              }}
                            >
                              Apagar
                            </button>
                            <button
                              type="button"
                              className="hov-bg-26"
                              style={GHOST_BTN}
                              onClick={() => setRoleDeleting(false)}
                            >
                              Cancelar
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={!canRoles}
                            onClick={() => setRoleDeleting(true)}
                            className={canRoles ? "hov-red" : undefined}
                            style={{
                              ...DANGER_BTN,
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              padding: "9px 12px",
                              cursor: clickable,
                              ...dim,
                            }}
                          >
                            <TrashIcon size={14} />
                            Apagar cargo
                          </button>
                        ))}
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <div style={{ ...MONO_LABEL, marginBottom: 8 }}>COR DO CARGO</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {ROLE_SWATCHES.map(color => (
                          <button
                            key={color}
                            type="button"
                            title={color}
                            disabled={!canRoles}
                            onClick={() => actions.updateRole(selectedRole.id, { color })}
                            style={{
                              width: 26,
                              height: 26,
                              padding: 0,
                              borderRadius: 8,
                              cursor: clickable,
                              background: color,
                              border: `2px solid ${
                                color === selectedRole.color ? C.text : "transparent"
                              }`,
                              ...dim,
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <div style={{ ...MONO_LABEL, marginBottom: 8 }}>
                        {`MEMBROS COM ESTE CARGO — ${roleMembersList.length}`}
                      </div>
                      {roleMembersList.length === 0 ? (
                        <div style={{ fontSize: 12.5, color: C.textLabel }}>
                          Nenhum membro com este cargo.
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {roleMembersList.map(member => (
                            <span
                              key={member.id}
                              title={atHandle(member.handle)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                                padding: "3px 11px 3px 3px",
                                border: `1px solid ${C.lineStrong}`,
                                borderRadius: 999,
                                background: C.field,
                                fontSize: 12.5,
                                color: C.textBody,
                              }}
                            >
                              <Avatar
                                url={member.avatarUrl}
                                name={member.name}
                                color={colorOf(state, member.id)}
                                size={20}
                                fontSize={10}
                              />
                              {member.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ ...MONO_LABEL, marginBottom: 6 }}>PERMISSÕES</div>
                    {PERMISSIONS.map(perm => {
                      const on = selectedRole.perms[perm.id];
                      return (
                        <div
                          key={perm.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            padding: "11px 2px",
                            borderBottom: `1px solid ${C.lineRow}`,
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span
                              style={{
                                display: "block",
                                fontSize: 13.5,
                                fontWeight: 600,
                                color: C.textStrong,
                              }}
                            >
                              {perm.name}
                            </span>
                            <span
                              style={{
                                display: "block",
                                fontSize: 11.5,
                                color: C.textFaint,
                                marginTop: 1,
                              }}
                            >
                              {PERM_DESC[perm.id] ?? perm.desc}
                            </span>
                          </span>
                          <button
                            type="button"
                            disabled={!canRoles}
                            aria-pressed={on}
                            title={perm.name}
                            onClick={() => actions.togglePermission(selectedRole.id, perm.id)}
                            style={{
                              width: 40,
                              height: 22,
                              flex: "0 0 auto",
                              position: "relative",
                              padding: 0,
                              border: 0,
                              borderRadius: 999,
                              cursor: clickable,
                              background: on ? "var(--ac, #1F4FD8)" : C.track,
                              transition: "background 150ms ease",
                              ...dim,
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                top: 3,
                                width: 16,
                                height: 16,
                                borderRadius: 999,
                                background: C.text,
                                transition: "left 150ms ease",
                                left: on ? 21 : 3,
                              }}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {state.settingsTab === "membros" &&
              serverMembers(state).map(user => {
                const color = colorOf(state, user.id);
                const owner = user.roleId === OWNER_ROLE_ID;
                const self = user.id === state.me?.id;
                const confirming = memberConfirm?.userId === user.id ? memberConfirm.action : null;
                const muteAllowed = canMute && !owner;
                const kickAllowed = canKick && !owner && !self;
                const banAllowed = canKick && !owner && !self;
                const timeoutAllowed = canMute && !owner && !self;
                const timedOut = isTimedOut(user);
                return (
                  <div
                    key={user.id}
                    style={{ padding: "12px 2px", borderBottom: `1px solid ${C.lineRow}` }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <Avatar
                        url={user.avatarUrl}
                        name={user.name}
                        color={color}
                        size={34}
                        fontSize={14}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {user.name}
                          </span>
                          {user.serverMuted && (
                            <span
                              title="Silenciado no servidor"
                              style={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                padding: "3px 8px",
                                border: `1px solid ${hexA(C.red, 0.3)}`,
                                borderRadius: 999,
                                background: hexA(C.red, 0.1),
                                fontFamily: FONT_MONO,
                                fontSize: 9,
                                fontWeight: 600,
                                letterSpacing: ".1em",
                                color: C.red,
                              }}
                            >
                              <MicIcon size={11} bare color={C.red} />
                              SILENCIADO
                            </span>
                          )}
                          {timedOut && (
                            <span
                              title={`Timeout até ${new Date(user.timeoutUntil ?? 0).toLocaleString("pt-BR")}`}
                              style={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                padding: "3px 8px",
                                border: `1px solid ${hexA(C.amber, 0.32)}`,
                                borderRadius: 999,
                                background: hexA(C.amber, 0.1),
                                fontFamily: FONT_MONO,
                                fontSize: 9,
                                fontWeight: 600,
                                letterSpacing: ".1em",
                                color: C.amber,
                              }}
                            >
                              <TimeoutIcon size={11} />
                              TIMEOUT
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontFamily: FONT_MONO,
                            fontSize: 10,
                            color: C.textLabel,
                            marginTop: 1,
                          }}
                        >
                          {atHandle(user.handle)}
                        </span>
                      </span>
                      <select
                        value={user.roleId}
                        disabled={!canRoles || owner}
                        title={
                          owner ? "O dono da comunidade mantém o cargo de fundador." : "Cargo"
                        }
                        onChange={event => actions.setMemberRole(user.id, event.target.value)}
                        style={{
                          padding: "7px 10px",
                          border: `1px solid ${C.lineStrong}`,
                          borderRadius: 8,
                          background: C.field,
                          color: C.textStrong,
                          fontSize: 12.5,
                          outline: 0,
                          cursor: cursorIf(canRoles && !owner),
                          ...dimIf(canRoles && !owner),
                        }}
                      >
                        {roles
                          // A comunidade tem um dono so: `fundador` so aparece
                          // na linha de quem ja o tem, para o `<select>` ter o
                          // proprio valor. Ninguem promove ninguem (nem a si
                          // mesmo) a dono por aqui.
                          .filter(role => role.id !== OWNER_ROLE_ID || owner)
                          .map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        disabled={!muteAllowed}
                        title={
                          owner
                            ? "O dono da comunidade não pode ser silenciado."
                            : user.serverMuted
                              ? "Reativar a voz"
                              : "Silenciar no servidor"
                        }
                        onClick={() => setMemberConfirm({ userId: user.id, action: "mute" })}
                        className={muteAllowed ? "hov-bg-26-fg" : undefined}
                        style={{
                          ...ICON_BTN,
                          color: user.serverMuted ? C.red : C.textMuted,
                          borderColor: user.serverMuted ? hexA(C.red, 0.32) : C.lineStrong,
                          background: user.serverMuted ? hexA(C.red, 0.1) : "transparent",
                          cursor: cursorIf(muteAllowed),
                          ...dimIf(muteAllowed),
                        }}
                      >
                        <MicIcon size={15} bare />
                      </button>
                      <button
                        type="button"
                        disabled={!kickAllowed}
                        title={
                          owner
                            ? "O dono da comunidade não pode ser expulso."
                            : self
                              ? "Você não pode se expulsar."
                              : "Expulsar da comunidade"
                        }
                        onClick={() => setMemberConfirm({ userId: user.id, action: "kick" })}
                        className={kickAllowed ? "hov-red" : undefined}
                        style={{
                          ...ICON_BTN,
                          color: C.red,
                          borderColor: hexA(C.red, 0.32),
                          cursor: cursorIf(kickAllowed),
                          ...dimIf(kickAllowed),
                        }}
                      >
                        <KickIcon size={15} />
                      </button>
                      <button
                        type="button"
                        disabled={!banAllowed}
                        title={
                          owner
                            ? "O dono da comunidade não pode ser banido."
                            : self
                              ? "Você não pode se banir."
                              : "Banir da comunidade"
                        }
                        onClick={() => setMemberConfirm({ userId: user.id, action: "ban" })}
                        className={banAllowed ? "hov-red" : undefined}
                        style={{
                          ...ICON_BTN,
                          color: C.red,
                          borderColor: hexA(C.red, 0.32),
                          cursor: cursorIf(banAllowed),
                          ...dimIf(banAllowed),
                        }}
                      >
                        <BanIcon size={15} />
                      </button>
                      <button
                        type="button"
                        disabled={!timeoutAllowed}
                        title={
                          owner
                            ? "O dono da comunidade não pode entrar em timeout."
                            : timedOut
                              ? "Encerrar o timeout agora"
                              : "Aplicar timeout (bloqueia texto e voz)"
                        }
                        onClick={() => setMemberConfirm({ userId: user.id, action: "timeout" })}
                        className={timeoutAllowed ? "hov-bg-26-fg" : undefined}
                        style={{
                          ...ICON_BTN,
                          color: timedOut ? C.amber : C.textMuted,
                          borderColor: timedOut ? hexA(C.amber, 0.32) : C.lineStrong,
                          background: timedOut ? hexA(C.amber, 0.1) : "transparent",
                          cursor: cursorIf(timeoutAllowed),
                          ...dimIf(timeoutAllowed),
                        }}
                      >
                        <TimeoutIcon size={15} />
                      </button>
                    </div>

                    {confirming && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 10,
                          marginTop: 10,
                          padding: "8px 10px",
                          border: `1px solid ${hexA(C.red, 0.28)}`,
                          borderRadius: 9,
                          background: hexA(C.red, 0.08),
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 180, fontSize: 12.5, color: C.textSoft }}>
                          {confirming === "kick"
                            ? `Expulsar ${user.name} da comunidade? A conta é removida e a sessão termina na hora.`
                            : confirming === "ban"
                              ? `Banir ${user.name} da comunidade? A conta é removida e o cadastro (usuário e e-mail) fica bloqueado para sempre.`
                              : confirming === "timeout"
                                ? timedOut
                                  ? `Encerrar o timeout de ${user.name} agora?`
                                  : `Aplicar timeout em ${user.name}? Ele não vai conseguir falar nem escrever até o prazo escolhido.`
                                : user.serverMuted
                                  ? `Devolver a voz de ${user.name} nas salas?`
                                  : `Silenciar ${user.name} no servidor? Ele não vai conseguir falar nas salas de voz.`}
                        </span>
                        {confirming === "timeout" && !timedOut && (
                          <select
                            value={timeoutMs}
                            onChange={event => setTimeoutMs(Number(event.target.value))}
                            style={{
                              padding: "7px 10px",
                              border: `1px solid ${C.lineStrong}`,
                              borderRadius: 8,
                              background: C.field,
                              color: C.textStrong,
                              fontSize: 12.5,
                              outline: 0,
                            }}
                          >
                            {TIMEOUT_PRESETS.map(preset => (
                              <option key={preset.label} value={preset.ms}>
                                {preset.label}
                              </option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          className="hov-bright-110"
                          style={DANGER_BTN}
                          onClick={() => {
                            if (confirming === "kick") actions.kickMember(user.id);
                            else if (confirming === "ban") actions.banMember(user.id);
                            else if (confirming === "timeout") {
                              actions.timeoutMember(user.id, timedOut ? null : Date.now() + timeoutMs);
                            } else actions.setServerMute(user.id, !user.serverMuted);
                            setMemberConfirm(null);
                          }}
                        >
                          {confirming === "kick"
                            ? "Expulsar"
                            : confirming === "ban"
                              ? "Banir"
                              : confirming === "timeout"
                                ? timedOut
                                  ? "Encerrar"
                                  : "Aplicar"
                                : user.serverMuted
                                  ? "Reativar"
                                  : "Silenciar"}
                        </button>
                        <button
                          type="button"
                          className="hov-bg-26"
                          style={GHOST_BTN}
                          onClick={() => setMemberConfirm(null)}
                        >
                          Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

            {state.settingsTab === "auditoria" && (
              <div>
                {auditLog === null && auditLogLoading && (
                  <div
                    style={{ padding: "28px 4px", textAlign: "center", fontSize: 12.5, color: C.textMuted }}
                  >
                    Carregando registros...
                  </div>
                )}
                {auditLog !== null && auditLog.length === 0 && (
                  <div
                    style={{ padding: "28px 4px", textAlign: "center", fontSize: 12.5, color: C.textMuted }}
                  >
                    Nenhuma ação de moderação registrada ainda.
                  </div>
                )}
                {auditLog?.map(entry => {
                  const meta = AUDIT_ACTION_META[entry.action];
                  return (
                    <div
                      key={entry.id}
                      style={{ padding: "10px 2px", borderBottom: `1px solid ${C.lineRow}` }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            flex: "0 0 auto",
                            padding: "3px 8px",
                            border: `1px solid ${hexA(meta.color, 0.32)}`,
                            borderRadius: 999,
                            background: hexA(meta.color, 0.1),
                            color: meta.color,
                            fontFamily: FONT_MONO,
                            fontSize: 9,
                            fontWeight: 600,
                            letterSpacing: ".08em",
                          }}
                        >
                          {meta.label}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 13,
                            color: C.textStrong,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <strong>{entry.actorName}</strong>
                          {entry.targetName && (
                            <>
                              {" → "}
                              <strong>{entry.targetName}</strong>
                            </>
                          )}
                        </span>
                        <span
                          style={{
                            flex: "0 0 auto",
                            fontFamily: FONT_MONO,
                            fontSize: 10.5,
                            color: C.textLabel,
                          }}
                        >
                          {new Date(entry.createdAt).toLocaleString("pt-BR")}
                        </span>
                      </div>
                      {(entry.detail || entry.reason) && (
                        <div style={{ marginTop: 4, marginLeft: 2, fontSize: 12, color: C.textSoft }}>
                          {entry.detail}
                          {entry.detail && entry.reason && " · "}
                          {entry.reason && `Motivo: ${entry.reason}`}
                        </div>
                      )}
                    </div>
                  );
                })}
                {auditLog !== null && auditLog.length > 0 && auditLog.length % AUDIT_PAGE_SIZE === 0 && (
                  <button
                    type="button"
                    className="hov-bg-26"
                    style={{ ...GHOST_BTN, marginTop: 12, width: "100%" }}
                    disabled={auditLogLoading}
                    onClick={() => requestAuditLog(auditLog[auditLog.length - 1].createdAt)}
                  >
                    {auditLogLoading ? "Carregando..." : "Carregar mais"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Porta de entrada do Painel dos Fundadores, dentro das Configurações.
 *
 * O painel em si é uma tela cheia, montada no `App`. Aqui fica só o convite —
 * e a explicação do que a pessoa está prestes a abrir, porque um botão que
 * ocupa a tela inteira não deveria surpreender ninguém.
 */
function EntradaDoPainel(): ReactElement {
  const { actions } = useApp();
  return (
    <div style={{ display: "grid", gap: 16, maxWidth: 560 }}>
      <section
        style={{
          padding: "16px 18px",
          border: `1px solid ${hexA(C.accent, 0.45)}`,
          borderRadius: 12,
          background: C.field,
        }}
      >
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: ".12em",
            color: C.accent,
            textTransform: "uppercase",
          }}
        >
          Área exclusiva dos fundadores
        </div>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: C.textStrong, margin: "8px 0 8px" }}>
          Painel dos Fundadores
        </h3>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: C.textSoft, margin: "0 0 14px" }}>
          Central de controle da plataforma: todas as contas, todos os servidores, moderação
          global, auditoria e os interruptores de emergência. Toda ação daqui fica registrada com
          seu nome.
        </p>
        <button
          type="button"
          onClick={() => {
            actions.closeSettings();
            actions.abrirPainelFundadores();
          }}
          style={{
            padding: "10px 18px",
            border: 0,
            borderRadius: 9,
            background: C.accent,
            color: C.ink,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Abrir o painel
        </button>
      </section>
    </div>
  );
}
